import { useEffect, useMemo, useState } from 'react'
import {
  Plus, Trash, PencilSimple, FloppyDisk, X, FileCsv, FileXls, FilePdf, ArrowsClockwise, Warning, Spinner
} from '@phosphor-icons/react'
import * as XLSX from 'xlsx'
import { supabase } from '../../lib/supabase'
import {
  uploadTempInvoices, analyzeInvoices, removeTempInvoices, archiveInvoices,
} from '../../lib/invoiceImport'
import { downloadCSV } from '../esg/exportHelpers'
import OperationalIROs from './OperationalIROs'

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
}
const CARD = { background: C.bg, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const FONT = "'DM Sans', sans-serif"

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

// Campos del formulario — agrupados por bloque
const FIELDS = [
  { group: 'Energía (kWh)', items: [
    { key: 'energy_kwh_total',      label: 'Total energía consumida',       unit: 'kWh' },
    { key: 'electricity_kwh',       label: 'Electricidad red',              unit: 'kWh' },
    { key: 'energy_kwh_renewable',  label: 'Energía renovable in situ (FV)',unit: 'kWh' },
    { key: 'gas_kwh',               label: 'Gas natural',                   unit: 'kWh' },
    { key: 'district_heat_kwh',     label: 'Calefacción urbana',            unit: 'kWh' },
  ]},
  { group: 'Coste facturado (€)', items: [
    { key: 'electricity_eur',   label: 'Factura electricidad',       unit: '€' },
    { key: 'gas_eur',           label: 'Factura gas natural',        unit: '€' },
    { key: 'district_heat_eur', label: 'Factura calefacción urbana', unit: '€' },
    { key: 'water_eur',         label: 'Factura agua',               unit: '€' },
  ]},
  { group: 'Agua (m³)', items: [
    { key: 'water_m3_total',     label: 'Total agua consumida',  unit: 'm³' },
    { key: 'water_m3_potable',   label: 'Agua potable red',      unit: 'm³' },
    { key: 'water_m3_reclaimed', label: 'Agua reutilizada / reciclada', unit: 'm³' },
  ]},
  { group: 'Residuos (t)', items: [
    { key: 'waste_tons_total',     label: 'Total residuos generados', unit: 't' },
    { key: 'waste_tons_recycled',  label: 'Reciclados / desviados',   unit: 't' },
    { key: 'waste_tons_landfill',  label: 'A vertedero',              unit: 't' },
    { key: 'waste_tons_hazardous', label: 'Peligrosos',               unit: 't' },
  ]},
  { group: 'Emisiones GEI (kg CO₂e)', items: [
    { key: 'scope1_kgco2e', label: 'Scope 1 (combustión in situ)',          unit: 'kgCO₂e' },
    { key: 'scope2_kgco2e', label: 'Scope 2 (electricidad comprada)',       unit: 'kgCO₂e' },
    { key: 'scope3_kgco2e', label: 'Scope 3 (Cat 1 materiales + Cat 5 residuos)', unit: 'kgCO₂e' },
  ]},
  { group: 'Calidad aire interior (IAQ)', items: [
    { key: 'iaq_co2_ppm_avg', label: 'CO₂ medio interior',  unit: 'ppm' },
    { key: 'iaq_pm25_avg',    label: 'PM2.5 medio',          unit: 'µg/m³' },
  ]},
]

const ALL_FIELD_KEYS = FIELDS.flatMap(g => g.items.map(i => i.key))

// INVOICE_PROMPT y callAnalyzer viven en ../../lib/invoiceImport.js —
// compartidos con CockpitContratista/Seguimiento (Consumos de obra).

function blankForm() {
  const now = new Date()
  return {
    reading_year: now.getFullYear(),
    reading_month: now.getMonth() + 1,
    source: 'manual',
    notes: '',
    ...Object.fromEntries(ALL_FIELD_KEYS.map(k => [k, ''])),
  }
}

// ── CSV parser muy simple (separador ; o , — detecta automáticamente) ──
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean)
  if (lines.length < 2) return { rows: [], error: 'CSV vacío o sin filas' }
  const sep = (lines[0].split(';').length > lines[0].split(',').length) ? ';' : ','
  const headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''))
  const rows = lines.slice(1).map(line => {
    const cells = line.split(sep).map(c => c.trim().replace(/^"|"$/g, ''))
    const obj = {}
    headers.forEach((h, i) => { obj[h] = cells[i] ?? '' })
    return obj
  })
  return { rows, headers, sep }
}

// ── Detector formato "wide" (1 fila × 12 columnas mensuales) ──
// Devuelve { detected, year, values: [{month, value}], originLabel } o null
function detectWideFormat(grid) {
  // grid = array de arrays (todas las celdas). Buscamos una fila header con celdas
  // tipo "01","02",..."12" o "Ene","Feb"... y a continuación una fila con números.
  const monthMap = { '01': 1, '02': 2, '03': 3, '04': 4, '05': 5, '06': 6, '07': 7, '08': 8, '09': 9, '10': 10, '11': 11, '12': 12,
    'ene': 1, 'enero': 1, 'feb': 2, 'febrero': 2, 'mar': 3, 'marzo': 3, 'abr': 4, 'abril': 4, 'may': 5, 'mayo': 5,
    'jun': 6, 'junio': 6, 'jul': 7, 'julio': 7, 'ago': 8, 'agosto': 8, 'sep': 9, 'sept': 9, 'septiembre': 9,
    'oct': 10, 'octubre': 10, 'nov': 11, 'noviembre': 11, 'dic': 12, 'diciembre': 12 }
  for (let r = 0; r < Math.min(grid.length, 10); r++) {
    const row = grid[r] || []
    const monthCols = []  // { col, month }
    for (let c = 0; c < row.length; c++) {
      const v = String(row[c] || '').trim().toLowerCase().replace('.', '')
      if (monthMap[v]) monthCols.push({ col: c, month: monthMap[v] })
    }
    if (monthCols.length < 6) continue  // necesitamos al menos 6 meses para considerar válido
    // Buscar fila de datos (próxima fila no vacía debajo)
    for (let dataR = r + 1; dataR < grid.length; dataR++) {
      const dataRow = grid[dataR] || []
      const values = monthCols.map(({ col, month }) => {
        const raw = dataRow[col]
        const num = parseFloat(String(raw).replace(',', '.'))
        return { month, value: isFinite(num) ? num : null }
      }).filter(v => v.value != null)
      if (values.length < 6) continue
      // Detectar año en celdas previas
      let year = null
      for (let yr = 0; yr <= r; yr++) {
        for (const cell of (grid[yr] || [])) {
          const m = String(cell || '').match(/(20\d{2})/)
          if (m) { year = parseInt(m[1]); break }
        }
        if (year) break
      }
      // Etiqueta de origen (primera celda no vacía de la fila de datos o título)
      const originLabel = grid[0]?.[0] ? String(grid[0][0]) : ''
      return { detected: true, year, values, originLabel, dataRow }
    }
  }
  return null
}

export default function MonitorizacionOcupacion({ project }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [form, setForm] = useState(blankForm)
  const [editingId, setEditingId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [wideImport, setWideImport] = useState(null)  // { year, values, originLabel, targetField }
  const [pdfImport, setPdfImport] = useState(null)    // { processing, invoices: [], error }

  const load = async () => {
    if (!project?.id) return
    setLoading(true); setError(null)
    const { data, error } = await supabase
      .from('building_sensors')
      .select('*')
      .eq('project_id', project.id)
      .order('reading_year', { ascending: false })
      .order('reading_month', { ascending: false })
    setLoading(false)
    if (error) { setError(error.message); return }
    setRows(data || [])
  }
  // load depende solo de project?.id (ya en deps).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [project?.id])

  const startNew = () => { setForm(blankForm()); setEditingId(null); setShowForm(true) }
  const startEdit = (r) => {
    const f = { ...blankForm(), ...r }
    Object.keys(f).forEach(k => { if (f[k] == null) f[k] = '' })
    setForm(f); setEditingId(r.id); setShowForm(true)
  }
  const cancelEdit = () => { setShowForm(false); setEditingId(null); setForm(blankForm()) }

  const handleSave = async () => {
    setError(null)
    if (!project?.id) { setError('Sin proyecto activo'); return }
    const { data: u } = await supabase.auth.getUser()
    const user_id = u?.user?.id
    if (!user_id) { setError('No autenticado'); return }
    // Sanitiza campos numéricos
    const payload = {
      project_id: project.id, user_id,
      reading_year: parseInt(form.reading_year) || new Date().getFullYear(),
      reading_month: parseInt(form.reading_month) || 1,
      source: form.source || 'manual',
      notes: form.notes || null,
    }
    ALL_FIELD_KEYS.forEach(k => {
      const v = form[k]
      payload[k] = (v === '' || v == null) ? null : parseFloat(v)
    })
    if (editingId) {
      const { error } = await supabase.from('building_sensors').update(payload).eq('id', editingId)
      if (error) { setError(error.message); return }
    } else {
      const { error } = await supabase.from('building_sensors').insert([payload])
      if (error) { setError(error.message); return }
    }
    cancelEdit(); load()
  }

  const handleImportPDFs = async (files) => {
    if (!files?.length) return
    if (!project?.id) { setError('Sin proyecto activo'); return }
    setPdfImport({ processing: true, invoices: [], error: null, fileNames: Array.from(files).map(f => f.name) })
    try {
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')
      const { file_infos, skipped } = await uploadTempInvoices(files, user_id, project.id)
      if (!file_infos.length) throw new Error('No se pudo procesar ningún archivo:\n• ' + skipped.join('\n• '))
      if (skipped.length) console.warn('PDFs descartados:', skipped)
      const invoices = await analyzeInvoices(file_infos)
      if (!invoices.length) throw new Error('La IA no detectó facturas legibles en los archivos. Verifica que sean facturas con consumo y periodo claro.')
      setPdfImport({ processing: false, invoices, error: null, fileInfos: file_infos, fileNames: file_infos.map(f => f.name) })
    } catch (e) {
      setPdfImport({ processing: false, invoices: [], error: e.message, fileNames: [] })
    }
  }

  // Cancela el panel de import y borra los PDFs temporales que aún no se han archivado
  const cancelPDFImport = async () => {
    await removeTempInvoices(pdfImport?.fileInfos || [])
    setPdfImport(null)
  }

  const confirmPDFImport = async () => {
    if (!pdfImport?.invoices?.length) return
    setImporting(true); setError(null); setImportResult(null)
    try {
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')
      // Aplicar filtro de año si está activo
      const yf = pdfImport.yearFilter
      const invoicesToImport = (!yf || yf === 'all')
        ? pdfImport.invoices
        : pdfImport.invoices.filter(i => parseInt(i.year) === yf)
      // Agrupar facturas por (year, month) — una factura por mes/tipo se merge en un payload
      const byPeriod = new Map()
      invoicesToImport.forEach(inv => {
        const key = `${inv.year}-${inv.month}`
        const acc = byPeriod.get(key) || {
          project_id: project.id, user_id,
          reading_year: parseInt(inv.year),
          reading_month: parseInt(inv.month),
          source: 'pdf', notes: '',
        }
        const c = parseFloat(inv.consumption)
        if (!isFinite(c)) return
        const eur = parseFloat(inv.amount_eur)
        if (inv.kind === 'electricity') {
          acc.electricity_kwh = (acc.electricity_kwh || 0) + c
          if (isFinite(eur)) acc.electricity_eur = (acc.electricity_eur || 0) + eur
        } else if (inv.kind === 'gas') {
          acc.gas_kwh = (acc.gas_kwh || 0) + c
          if (isFinite(eur)) acc.gas_eur = (acc.gas_eur || 0) + eur
        } else if (inv.kind === 'water') {
          acc.water_m3_total = (acc.water_m3_total || 0) + c
          if (isFinite(eur)) acc.water_eur = (acc.water_eur || 0) + eur
        } else if (inv.kind === 'district_heat') {
          acc.district_heat_kwh = (acc.district_heat_kwh || 0) + c
          if (isFinite(eur)) acc.district_heat_eur = (acc.district_heat_eur || 0) + eur
        }
        const noteBits = [acc.notes, `${inv.kind}: ${c}${inv.kind === 'water' ? 'm³' : 'kWh'}${inv.amount_eur ? ' (' + inv.amount_eur + '€)' : ''}${inv.supplier ? ' — ' + inv.supplier : ''}`].filter(Boolean)
        acc.notes = noteBits.join(' | ')
        byPeriod.set(key, acc)
      })
      // Mapeo source_filename → storage_path (de los PDFs aún en /temp_invoices/)
      const fileInfos = pdfImport.fileInfos || []
      const nameToPath = new Map()
      for (const fi of fileInfos) nameToPath.set(fi.name, fi.storage_path)

      // Mapeo (year-month) → Set de source_filenames que contribuyeron a ese mes
      const monthToSources = new Map()
      invoicesToImport.forEach(inv => {
        const key = `${inv.year}-${inv.month}`
        const set = monthToSources.get(key) || new Set()
        if (inv.source_filename) set.add(inv.source_filename)
        monthToSources.set(key, set)
      })

      const payloads = [...byPeriod.values()]

      // Merge: lee los `notes` ya guardados de esos meses y concatena (sin duplicar)
      // antes de hacer el UPSERT. Evita que un segundo import sobrescriba la cita
      // de un import anterior (limitación del UPSERT por filas).
      let existingPathsMap = new Map()
      if (payloads.length) {
        const years  = [...new Set(payloads.map(p => p.reading_year))]
        const months = [...new Set(payloads.map(p => p.reading_month))]
        const { data: existing, error: exErr } = await supabase
          .from('building_sensors')
          .select('reading_year, reading_month, notes, invoice_evidence_paths')
          .eq('project_id', project.id)
          .in('reading_year', years)
          .in('reading_month', months)
        if (!exErr) {
          const existingMap = new Map()
          for (const r of (existing || [])) {
            const k = `${r.reading_year}-${r.reading_month}`
            existingMap.set(k, r.notes || '')
            existingPathsMap.set(k, Array.isArray(r.invoice_evidence_paths) ? r.invoice_evidence_paths : [])
          }
          for (const p of payloads) {
            const key = `${p.reading_year}-${p.reading_month}`
            const prev = existingMap.get(key)
            if (prev) {
              const allBits = [...prev.split(' | '), ...(p.notes || '').split(' | ')]
                .map(s => s.trim()).filter(Boolean)
              p.notes = [...new Set(allBits)].join(' | ')
            }
          }
        }
      }

      // Mover PDFs de /temp_invoices/ a /invoices_archive/ y devolver mapa
      // source_filename → nuevo storage_path para inyectar en los payloads.
      const archivedMap = await archiveInvoices(fileInfos, user_id, project.id, 'evidences')

      // Inyectar invoice_evidence_paths en cada payload (concatenando con lo que ya hubiera)
      for (const p of payloads) {
        const key = `${p.reading_year}-${p.reading_month}`
        const sources = monthToSources.get(key) || new Set()
        const newPaths = [...sources].map(n => archivedMap.get(n)).filter(Boolean)
        const prevPaths = existingPathsMap.get(key) || []
        const allPaths = [...new Set([...prevPaths, ...newPaths])]
        if (allPaths.length) p.invoice_evidence_paths = allPaths
      }

      const { error } = await supabase.from('building_sensors').upsert(payloads, { onConflict: 'project_id,reading_year,reading_month' })
      if (error) throw error
      setImportResult({ ok: true, count: payloads.length })
      setPdfImport(null)
      load()
    } catch (e) {
      setImportResult({ ok: false, message: e.message })
    } finally {
      setImporting(false)
    }
  }

  const confirmWideImport = async () => {
    if (!wideImport) return
    setImporting(true); setError(null); setImportResult(null)
    try {
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')
      const year = parseInt(wideImport.year)
      if (!year) throw new Error('Año inválido')
      const field = wideImport.targetField
      if (!field) throw new Error('Selecciona un campo destino')
      const payloads = wideImport.values.map(({ month, value }) => ({
        project_id: project.id, user_id,
        reading_year: year,
        reading_month: month,
        source: 'csv',
        notes: `Importado de ${wideImport.originLabel || 'archivo wide'}`,
        [field]: value,
      }))
      const { error } = await supabase.from('building_sensors').upsert(payloads, { onConflict: 'project_id,reading_year,reading_month' })
      if (error) throw error
      setImportResult({ ok: true, count: payloads.length })
      setWideImport(null)
      load()
    } catch (e) {
      setImportResult({ ok: false, message: e.message })
    } finally {
      setImporting(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Borrar esta lectura?')) return
    const { error } = await supabase.from('building_sensors').delete().eq('id', id)
    if (error) { setError(error.message); return }
    load()
  }

  const handleExportCSV = () => {
    const cols = ['reading_year', 'reading_month', ...ALL_FIELD_KEYS, 'source', 'notes']
    downloadCSV(
      rows.length
        ? rows.map(r => Object.fromEntries(cols.map(c => [c, r[c] ?? ''])))
        : [Object.fromEntries(cols.map(c => [c, '']))],
      rows.length
        ? `building_sensors_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`
        : `plantilla_building_sensors.csv`
    )
  }

  const handleImportCSV = async (file) => {
    setImporting(true); setError(null); setImportResult(null); setWideImport(null)
    try {
      const name = (file.name || '').toLowerCase()
      let csvRows = []
      let grid = null
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const buf = await file.arrayBuffer()
        const wb = XLSX.read(buf, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false })
        csvRows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false })
        if (!csvRows.length && !grid.length) throw new Error('Hoja Excel vacía o sin filas de datos')
      } else {
        const text = await file.text()
        const parsed = parseCSV(text)
        if (parsed.error) throw new Error(parsed.error)
        csvRows = parsed.rows
        // Reconstruir grid para detección wide
        grid = [parsed.headers, ...parsed.rows.map(r => parsed.headers.map(h => r[h]))]
      }
      // ¿Tienen las filas el formato "long" esperado?
      const looksLong = csvRows.some(r => r.reading_year && r.reading_month)
      if (!looksLong && grid) {
        const wide = detectWideFormat(grid)
        if (wide?.detected && wide.values.length) {
          // Preselecciona el año detectado y el campo más probable (energy_kwh_renewable
          // si el título menciona generación / FV / solar / PV, sino electricity)
          const titleLow = String(wide.originLabel || '').toLowerCase()
          const isFV = /(generación|generacion|fotovolt|solar|\bfv\b|\bpv\b|renew)/i.test(titleLow)
          setWideImport({
            year: wide.year || new Date().getFullYear() - 1,
            values: wide.values,
            originLabel: wide.originLabel,
            targetField: isFV ? 'energy_kwh_renewable' : 'electricity_kwh',
          })
          setImporting(false)
          return
        }
      }
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')
      const payloads = csvRows.map(r => {
        const p = {
          project_id: project.id, user_id,
          reading_year: parseInt(r.reading_year) || null,
          reading_month: parseInt(r.reading_month) || null,
          source: r.source || 'csv',
          notes: r.notes || null,
        }
        ALL_FIELD_KEYS.forEach(k => {
          const v = r[k]
          p[k] = (v === '' || v == null) ? null : parseFloat(v)
        })
        return p
      }).filter(p => p.reading_year && p.reading_month)
      if (!payloads.length) throw new Error('Ninguna fila válida (necesita reading_year + reading_month)')
      // upsert por (project_id, year, month)
      const { error } = await supabase.from('building_sensors').upsert(payloads, { onConflict: 'project_id,reading_year,reading_month' })
      if (error) throw error
      setImportResult({ ok: true, count: payloads.length })
      load()
    } catch (e) {
      setImportResult({ ok: false, message: e.message })
    } finally {
      setImporting(false)
    }
  }

  // Resumen anual — coherente con la tabla "Lecturas registradas" (electricidad red, FV separadas)
  const summary = useMemo(() => {
    if (!rows.length) return null
    const yMap = new Map()
    rows.forEach(r => {
      const a = yMap.get(r.reading_year) || { year: r.reading_year, n: 0, kwh: 0, kwh_renew: 0, m3: 0, waste_t: 0 }
      a.n += 1
      a.kwh += +(r.electricity_kwh || 0)
      a.kwh_renew += +(r.energy_kwh_renewable || 0)
      a.m3 += +(r.water_m3_total || 0)
      a.waste_t += +(r.waste_tons_total || 0)
      yMap.set(r.reading_year, a)
    })
    return [...yMap.values()].sort((a, b) => b.year - a.year)
  }, [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: FONT }}>
      {/* Header */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>
            Monitorización Post-Ocupación · building_sensors
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>Lecturas mensuales del edificio en uso</div>
          <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>Alimenta automáticamente Report ESG · GRESB · Taxonomía · CSRD · Informe GRI · Capex/Opex · Panel Inversor</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={load} disabled={loading} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <ArrowsClockwise size={13} /> {loading ? 'Cargando…' : 'Refrescar'}
          </button>
          <button onClick={handleExportCSV} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <FileCsv size={13} /> {rows.length ? 'Exportar CSV' : 'Plantilla CSV'}
          </button>
          <label style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <FileXls size={13} /> Importar CSV / Excel
            <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleImportCSV(f); e.target.value = '' }} disabled={importing} />
          </label>
          <label style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: '5px', padding: '6px 11px', fontSize: '0.72rem', cursor: pdfImport?.processing ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: pdfImport?.processing ? 0.5 : 1 }}>
            {pdfImport?.processing ? <Spinner size={13} /> : <FilePdf size={13} />}
            {pdfImport?.processing ? 'Analizando…' : 'Subir facturas PDF'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }} onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) handleImportPDFs(fs) }} disabled={pdfImport?.processing} />
          </label>
          <button onClick={startNew} style={{ background: C.olive, color: '#FFFFFF', border: 'none', borderRadius: '5px', padding: '7px 13px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <Plus size={13} /> Nueva lectura
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#FFF1ED', border: `1px solid ${C.coral}`, color: C.coral, padding: '0.6rem 1rem', borderRadius: '6px', fontSize: '0.8rem' }}><Warning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {error}</div>}
      {importResult && (
        importResult.ok
          ? <div style={{ background: '#F0F5EC', border: `1px solid ${C.olive}`, color: '#2E6E3F', padding: '0.6rem 1rem', borderRadius: '6px', fontSize: '0.8rem' }}>✓ Importadas {importResult.count} lecturas (upsert por año+mes).</div>
          : <div style={{ background: '#FFF1ED', border: `1px solid ${C.coral}`, color: C.coral, padding: '0.6rem 1rem', borderRadius: '6px', fontSize: '0.8rem' }}>Error import: {importResult.message}</div>
      )}

      {/* Panel confirmación importación wide (1 fila × 12 meses) */}
      {wideImport && (
        <div style={{ ...CARD, borderLeft: `4px solid ${C.amber}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: C.text }}>Detectado archivo con 12 meses en columnas</h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.77rem', color: C.muted }}>
                {wideImport.originLabel ? <>Origen: <em>{wideImport.originLabel}</em>. </> : null}
                Confirma año + campo destino antes de importar las {wideImport.values.length} lecturas.
              </p>
            </div>
            <button onClick={() => setWideImport(null)} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
              <X size={14} /> Cancelar
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '0.875rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Año de las lecturas
              <input type="number" min={2000} max={2100} value={wideImport.year}
                onChange={e => setWideImport({ ...wideImport, year: e.target.value })}
                style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.85rem', fontFamily: "'IBM Plex Mono', monospace" }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Asignar valores a
              <select value={wideImport.targetField}
                onChange={e => setWideImport({ ...wideImport, targetField: e.target.value })}
                style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.82rem' }}>
                {FIELDS.map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.items.map(it => <option key={it.key} value={it.key}>{it.label} ({it.unit})</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
          </div>

          {/* Vista previa */}
          <div style={{ overflowX: 'auto', marginBottom: '0.875rem' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                  {wideImport.values.map(v => <th key={v.month} style={{ padding: '0.3rem 0.6rem', textAlign: 'center', borderRight: `1px solid ${C.border}` }}>{MONTHS[v.month - 1]}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {wideImport.values.map(v => <td key={v.month} style={{ padding: '0.3rem 0.6rem', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", color: C.text, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>{v.value.toLocaleString()}</td>)}
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button onClick={() => setWideImport(null)} style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '5px', padding: '7px 13px', fontSize: '0.8rem', cursor: 'pointer' }}>Cancelar</button>
            <button onClick={confirmWideImport} disabled={importing} style={{ background: C.olive, color: '#FFFFFF', border: 'none', borderRadius: '5px', padding: '7px 13px', fontSize: '0.8rem', fontWeight: 600, cursor: importing ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <FloppyDisk size={13} /> Importar {wideImport.values.length} lecturas
            </button>
          </div>
        </div>
      )}

      {/* Panel confirmación facturas PDF analizadas */}
      {pdfImport && !pdfImport.processing && (
        <div style={{ ...CARD, borderLeft: `4px solid ${C.amber}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: C.text }}>
                {pdfImport.error ? 'Error al analizar facturas' : `Facturas detectadas (${pdfImport.invoices.length})`}
              </h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.77rem', color: C.muted }}>
                {pdfImport.error
                  ? pdfImport.error
                  : `Revisa los valores extraídos y edítalos antes de importar. Las facturas del mismo mes se combinan en una sola lectura. Archivos analizados: ${pdfImport.fileNames.join(', ')}`}
              </p>
            </div>
            <button onClick={cancelPDFImport} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
              <X size={14} /> Cancelar
            </button>
          </div>

          {pdfImport.invoices.length > 0 && (
            <>
              {/* Filtro de año */}
              {(() => {
                const detectedYears = [...new Set(pdfImport.invoices.map(i => parseInt(i.year)))].sort((a, b) => b - a)
                if (detectedYears.length <= 1) return null
                const sel = pdfImport.yearFilter || 'all'
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.78rem', color: C.muted }}>Mostrar año:</span>
                    {['all', ...detectedYears].map(y => (
                      <button key={y} onClick={() => setPdfImport(p => ({ ...p, yearFilter: y }))}
                        style={{ background: sel === y ? C.text : C.bg, color: sel === y ? '#F5F0EA' : C.muted, border: `1px solid ${sel === y ? C.text : C.border}`, borderRadius: '4px', padding: '4px 10px', fontSize: '0.74rem', cursor: 'pointer', fontWeight: 500 }}>
                        {y === 'all'
                          ? `Todos (${pdfImport.invoices.length})`
                          : `${y} (${pdfImport.invoices.filter(i => parseInt(i.year) === y).length})`}
                      </button>
                    ))}
                    {sel !== 'all' && (
                      <button onClick={() => setPdfImport(p => ({ ...p, invoices: p.invoices.filter(i => parseInt(i.year) === sel), yearFilter: 'all' }))}
                        style={{ background: C.coral, color: '#FFF', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.72rem', cursor: 'pointer' }}>
                        Borrar otros años
                      </button>
                    )}
                  </div>
                )
              })()}
              <div style={{ overflowX: 'auto', marginBottom: '0.875rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Año</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Mes</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Tipo</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Consumo</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>€</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Comercializadora</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', width: '36px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pdfImport.invoices.map((inv, i) => {
                      const yf = pdfImport.yearFilter
                      if (yf && yf !== 'all' && parseInt(inv.year) !== yf) return null
                      const update = (patch) => setPdfImport(p => ({ ...p, invoices: p.invoices.map((x, j) => j === i ? { ...x, ...patch } : x) }))
                      const remove = () => setPdfImport(p => ({ ...p, invoices: p.invoices.filter((_, j) => j !== i) }))
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? C.bg : C.bgAlt, borderBottom: '1px solid #F0EBE2' }}>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <input type="number" value={inv.year} onChange={e => update({ year: e.target.value })} style={{ width: '70px', padding: '0.25rem 0.4rem', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '0.78rem', fontFamily: "'IBM Plex Mono', monospace" }} />
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <select value={inv.month} onChange={e => update({ month: parseInt(e.target.value) })} style={{ padding: '0.25rem 0.4rem', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '0.78rem' }}>
                              {MONTHS.map((m, idx) => <option key={idx} value={idx + 1}>{idx + 1} — {m}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <select value={inv.kind} onChange={e => update({ kind: e.target.value })} style={{ padding: '0.25rem 0.4rem', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '0.78rem' }}>
                              <option value="electricity">⚡ Electricidad</option>
                              <option value="water">💧 Agua</option>
                              <option value="gas">🔥 Gas</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>
                            <input type="number" step="0.01" value={inv.consumption} onChange={e => update({ consumption: e.target.value })} style={{ width: '90px', padding: '0.25rem 0.4rem', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '0.78rem', fontFamily: "'IBM Plex Mono', monospace", textAlign: 'right' }} />
                            <span style={{ fontSize: '0.7rem', color: C.muted, marginLeft: 4 }}>{inv.kind === 'water' ? 'm³' : 'kWh'}</span>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>
                            <input type="number" step="0.01" value={inv.amount_eur ?? ''} onChange={e => update({ amount_eur: e.target.value })} style={{ width: '85px', padding: '0.25rem 0.4rem', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '0.78rem', fontFamily: "'IBM Plex Mono', monospace", textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: C.muted }}>{inv.supplier || '—'}</td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>
                            <button onClick={remove} title="Descartar" style={{ background: 'transparent', border: 'none', color: C.coral, cursor: 'pointer' }}><Trash size={13} /></button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {(() => {
                const yf = pdfImport.yearFilter
                const visibleCount = (!yf || yf === 'all') ? pdfImport.invoices.length : pdfImport.invoices.filter(i => parseInt(i.year) === yf).length
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button onClick={cancelPDFImport} style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '5px', padding: '7px 13px', fontSize: '0.8rem', cursor: 'pointer' }}>Cancelar</button>
                    <button onClick={confirmPDFImport} disabled={importing || visibleCount === 0} style={{ background: C.olive, color: '#FFFFFF', border: 'none', borderRadius: '5px', padding: '7px 13px', fontSize: '0.8rem', fontWeight: 600, cursor: importing ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, opacity: visibleCount === 0 ? 0.5 : 1 }}>
                      <FloppyDisk size={13} /> Importar {visibleCount} facturas
                    </button>
                  </div>
                )
              })()}
            </>
          )}
        </div>
      )}

      {/* Resumen anual */}
      {summary && summary.length > 0 && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 0.7rem', fontSize: '0.92rem', color: C.text }}>Resumen anual</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Año</th>
                <th style={{ padding: '0.4rem 0.6rem', textAlign: 'center' }}>Meses</th>
                <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>Electricidad red (kWh)</th>
                <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>FV (kWh)</th>
                <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>Agua (m³)</th>
                <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>Residuos (t)</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((y, i) => (
                <tr key={y.year} style={{ background: i % 2 === 0 ? C.bg : C.bgAlt, borderBottom: '1px solid #F0EBE2' }}>
                  <td style={{ padding: '0.4rem 0.6rem', fontWeight: 600 }}>{y.year}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>{y.n}/12</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{Math.round(y.kwh).toLocaleString()}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{Math.round(y.kwh_renew).toLocaleString()}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{Math.round(y.m3).toLocaleString()}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{y.waste_t.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* IROs operativos (Fase 1) — bloque aditivo, no afecta al flujo existente */}
      <OperationalIROs project={project} rows={rows} />

      {/* Form modal */}
      {showForm && (
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem', paddingBottom: '0.5rem', borderBottom: `1px solid ${C.border}` }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', color: C.text }}>{editingId ? 'Editar lectura' : 'Nueva lectura mensual'}</h3>
            <button onClick={cancelEdit} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
              <X size={14} /> Cancelar
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.7rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Año
              <input type="number" min={2000} max={2100} value={form.reading_year} onChange={e => setForm({ ...form, reading_year: e.target.value })}
                style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.85rem', fontFamily: "'IBM Plex Mono', monospace" }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Mes
              <select value={form.reading_month} onChange={e => setForm({ ...form, reading_month: e.target.value })}
                style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.85rem' }}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{i + 1} — {m}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Fuente
              <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.85rem' }}>
                <option value="manual">Manual (factura)</option>
                <option value="bms">BMS / SCADA</option>
                <option value="api">API comercializadora</option>
                <option value="iot">Sensor IoT</option>
                <option value="csv">Importado CSV</option>
              </select>
            </label>
          </div>

          {FIELDS.map(group => (
            <div key={group.group} style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.7rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: '0.4rem' }}>{group.group}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
                {group.items.map(it => (
                  <label key={it.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontSize: '0.72rem', color: C.muted }}>
                    {it.label} <span style={{ color: '#B0A89E' }}>({it.unit})</span>
                    <input type="number" step="0.01" value={form[it.key]} onChange={e => setForm({ ...form, [it.key]: e.target.value })}
                      placeholder="—" style={{ padding: '0.35rem 0.55rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.82rem', fontFamily: "'IBM Plex Mono', monospace", color: C.text, background: C.bgAlt }} />
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.72rem', color: C.muted }}>
              Notas (incidencias, contexto del mes, etc.)
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                style={{ padding: '0.4rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.82rem', fontFamily: FONT, resize: 'vertical' }} />
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: `1px solid ${C.border}` }}>
            <button onClick={cancelEdit} style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '5px', padding: '7px 13px', fontSize: '0.8rem', cursor: 'pointer' }}>Cancelar</button>
            <button onClick={handleSave} style={{ background: C.olive, color: '#FFFFFF', border: 'none', borderRadius: '5px', padding: '7px 13px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <FloppyDisk size={13} /> {editingId ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Tabla lecturas */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.7rem', fontSize: '0.92rem', color: C.text }}>Lecturas registradas ({rows.length})</h3>
        {rows.length === 0
          ? <div style={{ color: C.muted, fontSize: '0.82rem', padding: '0.5rem 0' }}>
              Sin lecturas todavía. Pulsa <strong>Nueva lectura</strong> para añadir un mes, o <strong>Importar CSV</strong> si tienes un archivo con varias filas a la vez. Descarga la plantilla con el botón <strong>Plantilla CSV</strong>.
            </div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Periodo</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }} title="kWh comprados a la red eléctrica (de factura)">Electricidad red</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }} title="Importe factura eléctrica (€ IVA incl.)">€ Elec.</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }} title="kWh generados in situ (placas FV)">FV</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Agua</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }} title="Importe factura de agua (€ IVA incl.)">€ Agua</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Residuos</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Scope 1+2</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>Fuente</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', width: '90px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? C.bg : C.bgAlt, borderBottom: '1px solid #F0EBE2' }}>
                      <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {r.reading_year}-{String(r.reading_month).padStart(2, '0')} <span style={{ color: C.muted, fontWeight: 400 }}>({MONTHS[r.reading_month - 1]})</span>
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{r.electricity_kwh != null ? Math.round(r.electricity_kwh).toLocaleString() : '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", color: r.electricity_eur ? C.text : C.muted }}>{r.electricity_eur != null ? `${(+r.electricity_eur).toFixed(2)} €` : '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", color: r.energy_kwh_renewable ? C.olive : C.muted }}>{r.energy_kwh_renewable != null ? Math.round(r.energy_kwh_renewable).toLocaleString() : '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{r.water_m3_total != null ? Math.round(r.water_m3_total).toLocaleString() : '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", color: r.water_eur ? C.text : C.muted }}>{r.water_eur != null ? `${(+r.water_eur).toFixed(2)} €` : '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{r.waste_tons_total != null ? r.waste_tons_total.toFixed(1) : '—'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>
                        {(r.scope1_kgco2e != null || r.scope2_kgco2e != null)
                          ? `${(((r.scope1_kgco2e || 0) + (r.scope2_kgco2e || 0)) / 1000).toFixed(2)} t`
                          : '—'}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', fontSize: '0.7rem', color: C.muted }}>{r.source || 'manual'}</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                        <button onClick={() => startEdit(r)} title="Editar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted, padding: '3px 6px' }}><PencilSimple size={14} /></button>
                        <button onClick={() => handleDelete(r.id)} title="Borrar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.coral, padding: '3px 6px' }}><Trash size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* Pista CSV */}
      <div style={{ background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '0.9rem 1.1rem' }}>
        <div style={{ fontSize: '0.78rem', color: C.muted, lineHeight: 1.5 }}>
          <strong style={{ color: '#5C4A3A' }}>Formato CSV para importar:</strong> usa la plantilla descargable (botón <em>Plantilla CSV</em>) con los nombres exactos de columna. Mínimo necesitas <code style={{ background: C.bg, padding: '1px 4px', borderRadius: '3px', border: `1px solid ${C.border}` }}>reading_year</code> y <code style={{ background: C.bg, padding: '1px 4px', borderRadius: '3px', border: `1px solid ${C.border}` }}>reading_month</code>. El resto de columnas son opcionales — déjalas vacías si no tienes el dato. La importación hace <strong>upsert</strong> por (año, mes): si ya existe esa lectura, se actualiza con los nuevos valores. Separador <code>;</code> o <code>,</code> detectado automáticamente.
        </div>
      </div>
    </div>
  )
}
